import React, { useEffect, useMemo, useState } from "react";
import ClientInteractionsView from "./ClientInteractionsView";
import ClientInteractionModal from "./ClientInteractionModal";
import { clientInteractionsService } from "../../../../services/clientInteractions.service";
import { clientsService } from "../../../../services/clients.service";

export default function ClientInteractionsPage({ authData, logout }) {
  const [loading, setLoading] = useState(false);

  const [clients, setClients] = useState([]);
  const [interactions, setInteractions] = useState([]);

  const [clientIdFilter, setClientIdFilter] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const unwrap = (res) => {
    if (res && typeof res === "object" && "data" in res) return res.data;
    return res;
  };

  const loadClients = async () => {
    const res = await clientsService.list({});
    const list = unwrap(res);
    setClients(Array.isArray(list) ? list : []);
  };

  const loadInteractions = async (clientId = clientIdFilter) => {
    const res = await clientInteractionsService.list({
      clientId: clientId?.trim() ? clientId.trim() : undefined,
    });
    const list = unwrap(res);
    setInteractions(Array.isArray(list) ? list : []);
  };

  const refreshAll = async () => {
    setLoading(true);
    try {
      await Promise.all([loadClients(), loadInteractions()]);
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return;
      }
      alert(e?.message || "Failed to load client interactions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    setOpenModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    setEditing(null);
  };

  const createInteraction = async (payload) => {
    setLoading(true);
    try {
      await clientInteractionsService.create(payload);
      await loadInteractions();
      return true;
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return false;
      }
      alert(e?.message || "Create failed");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateInteraction = async (id, payload) => {
    setLoading(true);
    try {
      await clientInteractionsService.update(id, payload);
      await loadInteractions();
      return true;
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return false;
      }
      alert(e?.message || "Update failed");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteInteraction = async (id) => {
    const ok = window.confirm("Are you sure you want to delete this interaction?");
    if (!ok) return;

    setLoading(true);
    try {
      await clientInteractionsService.remove(id);
      await loadInteractions();
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return;
      }
      alert(e?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const clientMap = useMemo(() => {
    const m = new Map();
    (clients || []).forEach((c) => m.set(String(c.id), c));
    return m;
  }, [clients]);

  return (
    <>
      <ClientInteractionsView
        authData={authData}
        logout={logout}
        loading={loading}
        clients={clients}
        interactions={interactions}
        clientMap={clientMap}
        clientIdFilter={clientIdFilter}
        setClientIdFilter={setClientIdFilter}
        onApplyFilter={async () => {
          setLoading(true);
          try {
            await loadInteractions(clientIdFilter);
          } catch (e) {
            if (e?.message === "UNAUTHORIZED") {
              logout?.();
              return;
            }
            alert(e?.message || "Filter failed");
          } finally {
            setLoading(false);
          }
        }}
        onClearFilter={async () => {
          setClientIdFilter("");
          setLoading(true);
          try {
            await loadInteractions("");
          } catch (e) {
            if (e?.message === "UNAUTHORIZED") {
              logout?.();
              return;
            }
            alert(e?.message || "Failed");
          } finally {
            setLoading(false);
          }
        }}
        onRefresh={refreshAll}
        onCreateClick={openCreate}
        onEdit={openEdit}
        onDelete={deleteInteraction}
      />

      {openModal && (
        <ClientInteractionModal
          initialData={editing}
          clients={clients}
          loadingClients={loading && (!clients || clients.length === 0)}
          onClose={closeModal}
          onSubmit={async (payload) => {
            if (editing?.id) {
              const ok = await updateInteraction(editing.id, payload);
              if (ok) closeModal();
            } else {
              const ok = await createInteraction(payload);
              if (ok) closeModal();
            }
          }}
        />
      )}
    </>
  );
}
